import {
  sRGBEncoding,
  TextureLoader,
  EquirectangularReflectionMapping,
  Color,
} from "three";
import { getRandomElement } from "../../../lib/misc/util";
import { Context } from "../../context";
import { PoserClock, TimeSegment } from "../../util/clock";

const DAY_SKYBOX_VARIANTS = [
  "Skybox_A.jpg",
  "Skybox_B.jpg",
  "Skybox_C.jpg",
  "Skybox_D.jpg",
];
const SUNSET_SKYBOX_VARIANTS = [
  "SkyboxSunset_A.jpg",
  "SkyboxSunset_B.jpg",
  "SkyboxSunset_C.jpg",
  "SkyboxSunset_D.jpg",
  "SkyboxSunset_E.jpg",
];
const NIGHT_SKYBOX_VARIANTS = [
  "SkyboxNight_A.jpg",
  "SkyboxNight_B.jpg",
  "SkyboxNight_C.jpg",
];

export class Skybox {
  state?: TimeSegment;

  daySkybox: string;
  sunsetSkybox: string;
  nightSkybox: string;
  context: Context;

  constructor(context: Context) {
    this.context = context;
    this.daySkybox = getRandomElement(DAY_SKYBOX_VARIANTS);
    this.sunsetSkybox = getRandomElement(SUNSET_SKYBOX_VARIANTS);
    this.nightSkybox = getRandomElement(NIGHT_SKYBOX_VARIANTS);
  }

  update(timeOfDay: TimeSegment) {
    this.transition(timeOfDay);
  }

  makeSky(filename: string, assetBaseOverride?: string) {
    const assetBase = assetBaseOverride ?? this.context.options.assetBase;
    const scene = this.context.scene;

    const loader = new TextureLoader();
    if (assetBase) {
      loader.setPath(assetBase);
    }
    const skyTexture = loader.load(filename);

    skyTexture.encoding = sRGBEncoding;
    skyTexture.mapping = EquirectangularReflectionMapping;
    scene.background = skyTexture;
    scene.environment = skyTexture;
  }

  private getSkybox(state: TimeSegment): string {
    switch (state) {
      case TimeSegment.NIGHT:
        return this.nightSkybox;
      case TimeSegment.SUNSET:
        return this.sunsetSkybox;
      case TimeSegment.DAY:
        return this.daySkybox;
      default:
        return this.sunsetSkybox;
    }
  }

  private getHemisphereLighting(state: TimeSegment): {
    intensity: number;
    skyColor: number;
    groundColor: number;
  } {
    switch (state) {
      case TimeSegment.NIGHT:
      case TimeSegment.SUNSET:
        return {
          intensity: 5,
          skyColor: 0xa8b1e1,
          groundColor: 0x1e2022,
        };
      default:
        return {
          intensity: 0.5,
          skyColor: 0x2c56c0,
          groundColor: 0x253e25,
        };
    }
  }

  private transition(newState: TimeSegment): void {
    if (this.state === newState) return;

    this.state = newState;

    const newSkybox = this.getSkybox(newState);
    const newHemiDetails = this.getHemisphereLighting(newState);

    // Update skybox
    this.makeSky(`/images/skyboxes/${newSkybox}`);

    // TODO: pull out transitioner and hemiLight controls into LightRig
    // Update hemisphere lighting
    if (this.context.hemiLight) {
      this.context.hemiLight.intensity = newHemiDetails.intensity;
      this.context.hemiLight.groundColor = new Color(
        newHemiDetails.groundColor
      );
      this.context.hemiLight.color = new Color(newHemiDetails.skyColor);
    }
  }
}
